import { Images } from '../assets'
import React from 'react'
import { Link } from 'react-router-dom'
import PageTitle from '../components/PageTitle'

const ServiceGallery = ({ title, images }) => (
  <div className="grid grid-cols-1 gap-3">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="group relative w-full h-[200px] md:h-[220px] rounded-xl overflow-hidden border border-[#E6E6E6]">
        <img
          src={images[0]}
          alt={`${title} 1`}
          className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
      </div>
      <div className="group relative w-full h-[200px] md:h-[220px] rounded-xl overflow-hidden border border-[#E6E6E6]">
        <img
          src={images[1]}
          alt={`${title} 2`}
          className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,0.3fr)_minmax(0,0.7fr)] gap-3">
      <div className="group relative w-full h-[200px] md:h-[220px] rounded-xl overflow-hidden border border-[#E6E6E6]">
        <img
          src={images[2]}
          alt={`${title} 3`}
          className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
      </div>
      <div className="group relative w-full h-[200px] md:h-[220px] rounded-xl overflow-hidden border border-[#E6E6E6]">
        <img
          src={images[3]}
          alt={`${title} 4`}
          className="w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3D6E]/70 via-[#0D5BA8]/40 to-transparent transition-opacity duration-500 group-hover:opacity-60" />
      </div>
    </div>
  </div>
)

const services = [
  {
    title: 'Digital Health Systems',
    images: [Images.EhrDhs1, Images.EhrDhs2, Images.EhrDhs3, Images.EhrDhs4],
    desc: 'We design and build the platforms that run health programmes – patient registries, laboratory tracking systems, civil registration platforms, mobile data collection tools, and vaccination trackers. We have delivered systems at the facility level, the state level, and the national level for government ministries, INGOs, and bilateral donors.',
    link: '/services/digital-health-systems'
  },
  {
    title: 'Health Data and Analytics',
    images: [Images.Apin01, Images.HealthDataAnalytics2, Images.EhrHda1, Images.EhrHda4],
    desc: 'We deploy data collection platforms at scale, configure real-time analytics dashboards, and generate the evidence that programme managers and donors need to make decisions. Our data deployments have collected over 120,000 beneficiary responses across more than 200 health facilities.',
    link: '/services/health-data-analytics'
  },
  {
    title: 'Implementation and Advisory',
    images: [Images.EhrIa3, Images.EhrIa5, Images.EhrIa1, Images.EhrIa4],
    desc: 'We assess existing systems, evaluate platform performance in real-world settings, modernise legacy infrastructure, train teams, and provide ongoing technical support. We stay engaged long after go-live because deployment is not the end of a project – it is the beginning.',
    link: '/services/implementation-advisory'
  }
]

const whyPoints = [
  {
    title: 'We know the context',
    desc: 'Our systems are built for the environments where they are used – offline-first where connectivity is unreliable, multilingual where populations are diverse, and integrated with existing government infrastructure rather than replacing it.',
    icon: Images.AssessmentIcon
  },
  {
    title: 'We have done it before',
    desc: 'We have delivered digital health systems for UNICEF, the Bill and Melinda Gates Foundation, the Clinton Health Access Initiative, IHVN, SmileTrain, the Global Fund, and federal and state ministries of health. Our project portfolio spans HIV, tuberculosis, cancer detection, civil registration, laboratory services, and vaccination tracking.',
    icon: Images.TrophyIcon
  },
  {
    title: 'We stay engaged',
    desc: 'We do not hand over a system and disappear. We train staff, support adoption, monitor performance, and remain available throughout the life of the programme.',
    icon: Images.MaintenanceIcon
  }
]

const Services = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  return (
    <>
      <PageTitle
        title="Health IT Services and Digital Health Consulting | SeamHealth Innovations"
        description="SeamHealth provides digital health systems, health data and analytics, and implementation advisory services for governments, INGOs, and bilateral donors across Sub-Saharan Africa."
      />

      {/* Hero */}
      <section className="relative min-h-[52vh] md:min-h-[45vh] flex items-center overflow-hidden">
        <video
          id="services-background-video"
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline="true"
          controls={false}
          disablePictureInPicture
        >
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-[#001630]/95 via-[#002A4D]/90 to-[#004A7C]/80" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12 w-full py-24 md:py-32">
          <div className="max-w-[760px]">
            <h1 data-aos="fade-up" className="text-5xl font-semibold text-white leading-tight mb-6">
              What we do
            </h1>
            <p data-aos="fade-up" className="text-xs md:text-xs text-white/80 max-w-[640px]" style={{ lineHeight: '20px' }}>
              Our services are organised around three capabilities that reflect how our clients actually engage
              us: building the systems that run their programmes, generating the data and evidence their
              programmes need, and advising on existing systems to improve how they perform. Most of our
              engagements draw on more than one of these capabilities at the same time.
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 h-32 md:h-44">
          <svg
            viewBox="0 0 1440 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full h-full block"
          >
            <path d="M0 200H1440V0L0 200Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ─── Section 1: Our Services ───────────────────────────────────────────────
          Each service card sits in its own padded container so the spacing is
          explicit and consistent. The card interior uses a fixed 40 / 60 column
          split regardless of which column the text falls in.
      ──────────────────────────────────────────────────────────────────────────── */}
      <section
        id="services-section"
        className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-8 xl:px-10"
      >
        {services.map((s, i) => (
          <div
            key={i}
            data-aos="fade-up"
            /* 
              py-16 md:py-24 on each card gives ~64–96 px above and below.
              The first card gets extra top padding to breathe away from the hero
              wave; the last one gets extra bottom padding before the spacer div.
            */
            className={`
              py-16 md:py-20
              ${i === 0 ? 'pt-20 md:pt-32' : ''}
              ${i === services.length - 1 ? 'pb-20 md:pb-32' : ''}
            `}
          >
            <div className="rounded-xl bg-white h-full p-4 sm:p-5 md:p-8">
              {/*
                Fixed 40 / 60 split for ALL cards.
                Text is always in the 40% slot; gallery is always in the 60% slot.
                We achieve alternating left/right layout by rendering the two
                columns in different DOM order per card — no CSS order tricks
                that would break the column-width mapping.
                  Even cards (0, 2): [text 40%] [gallery 60%]
                  Odd  cards (1):    [gallery 60%] [text 40%]  ← grid flipped
              */}
              {i % 2 === 0 ? (
                /* Even: text left (40%) · gallery right (60%) */
                <div className="grid gap-6 md:gap-10 md:grid-cols-[minmax(0,0.4fr)_minmax(0,0.6fr)]">
                  <div className="flex flex-col justify-center md:sticky md:top-24 md:self-start">
                    <h1 className="text-5xl text-[#2A2A2A] mb-4">{s.title}</h1>
                    <p className="text-xs md:text-xs text-[#5B5B5B]" style={{ lineHeight: '20px' }}>{s.desc}</p>
                    <Link to={s.link} className="mt-6 flex items-center gap-2 text-[#007DD6] text-base font-medium hover:gap-3 transition-all">
                      Learn more{' '}
                      <img decoding="async" src={Images.ArrowUpRight} alt="" className="w-4 h-4" loading="lazy" />
                    </Link>
                  </div>
                  <div>
                    <ServiceGallery title={s.title} images={s.images} />
                  </div>
                </div>
              ) : (
                /* Odd: gallery left (60%) · text right (40%) */
                <div className="grid gap-6 md:gap-10 md:grid-cols-[minmax(0,0.6fr)_minmax(0,0.4fr)]">
                  <div>
                    <ServiceGallery title={s.title} images={s.images} />
                  </div>
                  <div className="flex flex-col justify-center md:sticky md:top-24 md:self-start">
                    <h1 className="text-5xl text-[#2A2A2A] mb-4">{s.title}</h1>
                    <p className="text-xs md:text-xs text-[#5B5B5B]" style={{ lineHeight: '20px' }}>{s.desc}</p>
                    <Link to={s.link} className="mt-6 flex items-center gap-2 text-[#007DD6] text-base font-medium hover:gap-3 transition-all">
                      Learn more{' '}
                      <img decoding="async" src={Images.ArrowUpRight} alt="" className="w-4 h-4" loading="lazy" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </section>

      <div className="h-24 md:h-32" />

      {/* ─── Section 2: Why Work With Us ──────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-8 md:px-12 py-12 md:py-12">
        <h1 data-aos="fade-up" className="text-5xl font-bold text-[#2A2A2A] mb-16">
          Why Work With Us
        </h1>
        <div className="grid md:grid-cols-3 gap-8">
          {whyPoints.map((point, i) => (
            <div
              key={i}
              data-aos="fade-up"
              className="flex flex-col rounded-2xl border border-[#E6E6E6] bg-white p-6 md:p-8 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="w-14 h-14 rounded-full bg-[#007DD6]/10 flex items-center justify-center mb-6">
                <img
                  decoding="async"
                  src={point.icon}
                  alt=""
                  className="w-7 h-7 brightness-75 saturate-150"
                  loading="lazy"
                />
              </div>
              <h5 className="text-lg md:text-xl font-semibold text-[#2A2A2A]">
                {point.title}
              </h5>
              <p className="text-base text-[#5B5B5B] mt-3 leading-relaxed">
                {point.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="h-24 md:h-32" />

      {/* ─── Section 3: CTA ───────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-[#001630] to-[#002A4D] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-[1] opacity-[0.2]"
        >
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-[2] bg-gradient-to-br from-[#001630]/60 to-[#002A4D]/60" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="grid gap-12 items-stretch min-h-[420px] md:min-h-[520px]">
            <div className="flex flex-col justify-center py-12 md:py-20">
              <h1
                data-aos="fade-up"
                className="text-5xl text-white tracking-tight"
              >
                Ready to work with us?
              </h1>
              <p
                data-aos="fade-up"
                className="text-lg text-white/70 mt-4 leading-relaxed max-w-[480px]"
              >
                Tell us about your programme and we will tell you how we can help.
              </p>
              <div data-aos="fade-up" className="mt-8">
                <Link
                  to="/get-in-touch"
                  className="btn btn-primary text-base w-fit"
                >
                  Get in touch{' '}
                  <img decoding="async" src={Images.ArrowUpRight} alt="" loading="lazy" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Services